import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, Renderer2, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthorizeService } from 'src/api-authorization/authorize.service';
import { CaseResponse } from './case-response.model';

@Component({
	selector: 'jfriedman-home',
	templateUrl: './home.component.html',
	styleUrls: ['home.component.scss']
})
export class JFriedmanHomeComponent implements OnInit, OnDestroy, AfterViewInit {
	public CASE_NUMBER: string = '%CASE_NUMBER%';
	public COURT_TYPE: string = '%COURT_TYPE%';
	public CASE_TYPE: string = '%CASE_TYPE%';
	public JUDGE: string = '%JUDGE%';
	public DEFENDANT: string = '%DEFENDANT%';
	public DEFENDANT_FIRST_LAST: string = '%DEFENDANT_FIRST_LAST%';
	public DEFENDANT_SEX: string = '%DEFENDANT_SEX%';
	public CHARGES: string = '%CHARGES%';
	public JUDICIAL_CIRCUIT: string = '%JUDICIAL_CIRCUIT%';
	public COUNTY: string = '%COUNTY%';
	public COUNTY_ALL_CAPS: string = '%COUNTY_ALL_CAPS%';
	public TODAY_DATE: string = '%TODAY_DATE%';
	public caseNumber: string = '';
	public caseResponse: CaseResponse | null = null;
	public loading: boolean = false;
	public loadError: string = '';
	public caseForm: FormGroup;
	public googleDocs: any[] = [];
	public loadingDocs: boolean = false;
	public docsError: string = '';
	public selectedTemplateId: string = '';
	public generatingDoc: boolean = false;
	public generateError: string = '';
	public generatedDocUrl: string = '';
	public editingField: string | null = null;
	public editingFieldName: string | null = null;
	public formFields: Array<{ key: string; label: string; isCustom?: boolean }> = [];
	public customFieldCounter: number = 0;

	@ViewChildren('valueInput') valueInputs!: QueryList<ElementRef>;
	@ViewChildren('nameInput') nameInputs!: QueryList<ElementRef>;

	constructor(private route: ActivatedRoute,
		private router: Router,
		private http: HttpClient,
		private sanitizer: DomSanitizer,
		private authSvc: AuthorizeService,
		private renderer: Renderer2,
		private fb: FormBuilder,
		@Inject(DOCUMENT) private document: Document) {

		// Initialize the reactive form
		this.caseForm = this.fb.group({
			[this.CASE_NUMBER]: [''],
			[this.COURT_TYPE]: [''],
			[this.CASE_TYPE]: [''],
			[this.JUDGE]: [''],
			[this.DEFENDANT]: [''],
			[this.DEFENDANT_FIRST_LAST]: [''],
			[this.DEFENDANT_SEX]: [''],
			[this.CHARGES]: [''],
			[this.JUDICIAL_CIRCUIT]: [''],
			[this.COUNTY]: [''],
			[this.COUNTY_ALL_CAPS]: [''],
			[this.TODAY_DATE]: [this.formatDate(new Date())]
		});

		// Initialize form fields array for display
		this.formFields = [
			{ key: this.CASE_NUMBER, label: this.CASE_NUMBER },
			{ key: this.COURT_TYPE, label: this.COURT_TYPE },
			{ key: this.CASE_TYPE, label: this.CASE_TYPE },
			{ key: this.JUDGE, label: this.JUDGE },
			{ key: this.DEFENDANT, label: this.DEFENDANT },
			{ key: this.DEFENDANT_FIRST_LAST, label: this.DEFENDANT_FIRST_LAST },
			{ key: this.DEFENDANT_SEX, label: this.DEFENDANT_SEX },
			{ key: this.CHARGES, label: this.CHARGES },
			{ key: this.JUDICIAL_CIRCUIT, label: this.JUDICIAL_CIRCUIT },
			{ key: this.COUNTY, label: this.COUNTY },
			{ key: this.COUNTY_ALL_CAPS, label: this.COUNTY_ALL_CAPS },
			{ key: this.TODAY_DATE, label: this.TODAY_DATE }
		];

		const elm = this.document.getElementById("nav-menu");
		if (elm) this.renderer.addClass(elm, 'hideme');
		const elm2 = this.document.getElementById("app-footer");
		if (elm2) this.renderer.addClass(elm2, 'hideme');
	}

	public ngOnDestroy(): void {
		const elm = this.document.getElementById("nav-menu");
		if (elm) this.renderer.removeClass(elm, 'hideme');
		const elm2 = this.document.getElementById("app-footer");
		if (elm2) this.renderer.removeClass(elm2, 'hideme');
	}

	ngAfterViewInit(): void {
		// Subscribe to changes in the valueInputs QueryList to auto-focus when input appears
		this.valueInputs.changes.subscribe(() => {
			if (this.valueInputs.first) {
				setTimeout(() => this.valueInputs.first.nativeElement.focus(), 0);
			}
		});
		
		// Subscribe to changes in the nameInputs QueryList to auto-focus when input appears
		this.nameInputs.changes.subscribe(() => {
			if (this.nameInputs.first) {
				setTimeout(() => this.nameInputs.first.nativeElement.focus(), 0);
			}
		});
	}

	async ngOnInit(): Promise<void> {
		// enforce auth like other pages
		var token = await this.authSvc.getToken();
		if (token.sub != "acfc2497-cb44-4394-b866-3f546b306ff6"
			&& token.sub != "75188666-0c61-4d26-b4d1-d184befa3afb" // Alec Lubin
			&& token.sub != '074e9283-ff17-49c1-b6f8-787d11dad581'
			&& token.sub != '75188666-0c61-4d26-b4d1-d184befa3afb'
			&& token.sub != 'ff08b278-700d-4d51-a589-42034ab6c925' // Steve Cohn
		) this.router.navigate(['/home']);

		this.caseNumber = this.route.snapshot.paramMap.get('casenumber') || '';
		if (!this.caseNumber) return;

		this.loading = true;
		this.http.get(`/jfriedman/case/${encodeURIComponent(this.caseNumber)}`).subscribe(
			(resp: any) => {
				this.caseResponse = CaseResponse.fromJson(resp);
				this.populateForm(this.caseResponse?.caseDetails);
				this.loading = false;
			},
			(err: any) => {
				this.loadError = err?.error || 'Unable to load case details';
				this.loading = false;
			}
		);

		// Load Google Docs list
		this.loadGoogleDocs();
	}

	private loadGoogleDocs(): void {
		this.loadingDocs = true;
		this.http.get('/jfriedman/documentList').subscribe(
			(resp: any) => {
				this.googleDocs = resp || [];
				this.loadingDocs = false;
			},
			(err: any) => {
				const errorMessage = err?.error || 'Unable to load Google Docs list';
				
				// Check if the error is due to missing access token
				if (typeof errorMessage === 'string' && errorMessage.includes('Access token not found in session')) {
					this.router.navigate(['/jfriedman/login']);
					return;
				}
				
				this.docsError = errorMessage;
				this.loadingDocs = false;
			}
		);
	}

	safe(text: any) {
		return this.sanitizer.bypassSecurityTrustHtml(text);
	}

	private formatDate(date: Date): string {
		const day = date.getDate();
		const month = date.toLocaleString('en-US', { month: 'long' });
		const year = date.getFullYear();
		
		// Get ordinal suffix for day
		const suffix = this.getOrdinalSuffix(day);
		
		return `${day}${suffix} day of ${month}, ${year}`;
	}

	private getOrdinalSuffix(day: number): string {
		if (day > 3 && day < 21) return 'th';
		switch (day % 10) {
			case 1: return 'st';
			case 2: return 'nd';
			case 3: return 'rd';
			default: return 'th';
		}
	}

	private populateForm(caseDetails: any): void {
		if (caseDetails) {
			this.caseForm.patchValue({
				[this.CASE_NUMBER]: caseDetails.caseNumber || '',
				[this.COURT_TYPE]: caseDetails.courtType || '',
				[this.CASE_TYPE]: caseDetails.caseType || '',
				[this.JUDGE]: caseDetails.judge || '',
				[this.DEFENDANT]: caseDetails.defendant || '',
				[this.DEFENDANT_FIRST_LAST]: caseDetails.defendantFirstLast || '',
				[this.DEFENDANT_SEX]: caseDetails.defendantSex || '',
				[this.CHARGES]: caseDetails.charges.join(', ') || '',
				[this.JUDICIAL_CIRCUIT]: caseDetails.judicialCircuit || '',
				[this.COUNTY]: caseDetails.county || '',
				[this.COUNTY_ALL_CAPS]: caseDetails.countyAllCaps || ''
			});
		}
	}

	public startEdit(fieldKey: string): void {
		this.editingField = fieldKey;
	}

	public stopEdit(): void {
		this.editingField = null;
	}

	public isEditing(fieldKey: string): boolean {
		return this.editingField === fieldKey;
	}

	public startEditName(fieldKey: string): void {
		this.editingFieldName = fieldKey;
	}

	public stopEditName(): void {
		this.editingFieldName = null;
	}

	public isEditingName(fieldKey: string): boolean {
		return this.editingFieldName === fieldKey;
	}

	public getFieldValue(fieldKey: string): string {
		return this.caseForm.get(fieldKey)?.value || '';
	}

	public updateFieldValue(fieldKey: string, value: string): void {
		this.caseForm.patchValue({ [fieldKey]: value });
	}

	public updateFieldName(fieldKey: string, newName: string): void {
		const field = this.formFields.find(f => f.key === fieldKey);
		if (field && field.isCustom) {
			field.label = newName;
		}
	}

	public isValidFieldName(name: string): boolean {
		if (!name || name.trim() === '') {
			return true; // Empty is allowed (will be filtered out during document generation)
		}
		// Must start and end with %, contain only uppercase letters and underscores
		const pattern = /^%[A-Z_]+%$/;
		return pattern.test(name);
	}

	public getFieldNameValidationError(fieldKey: string): string | null {
		const field = this.formFields.find(f => f.key === fieldKey);
		if (!field || !field.isCustom) {
			return null;
		}
		
		const name = field.label;
		if (!name || name.trim() === '') {
			return null; // Empty is valid
		}
		
		if (!this.isValidFieldName(name)) {
			return 'Name must be in format: %NAME_WITH_UNDERSCORES% (uppercase only)';
		}
		
		return null;
	}

	public addCustomField(): void {
		const newKey = `CUSTOM_FIELD_${this.customFieldCounter++}`;
		this.formFields.push({ 
			key: newKey, 
			label: '', 
			isCustom: true 
		});
		this.caseForm.addControl(newKey, this.fb.control(''));
	}

	public deleteCustomField(fieldKey: string): void {
		const index = this.formFields.findIndex(f => f.key === fieldKey);
		if (index !== -1 && this.formFields[index].isCustom) {
			this.formFields.splice(index, 1);
			this.caseForm.removeControl(fieldKey);
		}
	}

	public getFieldLabel(fieldKey: string): string {
		const field = this.formFields.find(f => f.key === fieldKey);
		return field?.label || '';
	}

	public hasValidationErrors(): boolean {
		return this.formFields.some(field => {
			if (field.isCustom) {
				const error = this.getFieldNameValidationError(field.key);
				return error !== null;
			}
			return false;
		});
	}

	public generateDocument(): void {
		if (!this.selectedTemplateId) {
			this.generateError = 'Please select a template';
			return;
		}

		this.generatingDoc = true;
		this.generateError = '';
		this.generatedDocUrl = '';

		// Build the replacement dictionary from form data
		const formValues = this.caseForm.value;
		const replacementData: { [key: string]: string } = {};
		
		// Add standard fields
		Object.keys(formValues).forEach(key => {
			const field = this.formFields.find(f => f.key === key);
			if (field) {
				if (field.isCustom) {
					// Only include custom fields if they have a non-blank name
					if (field.label && field.label.trim() !== '') {
						replacementData[field.label] = formValues[key];
					}
				} else {
					replacementData[key] = formValues[key];
				}
			}
		});
		
		// Ensure TODAY_DATE is formatted
		replacementData[this.TODAY_DATE] = this.formatDate(new Date());

		const payload = {
			templateId: this.selectedTemplateId,
			replacementData: replacementData
		};

		this.http.post('/jfriedman/generategoogledoc', payload).subscribe(
			(resp: any) => {
				this.generatedDocUrl = resp.documentLink || '';
				this.generatingDoc = false;
			},
			(err: any) => {
				this.generateError = err?.error || 'Failed to generate document';
				this.generatingDoc = false;
			}
		);
	}
}


